import { devices_config } from '@devices/index';
import { BLE_BUTTON_MODELS } from 'src/constants';
import { condition_to_character } from 'src/helpers/handle_manage_old_scenes';
import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
import {
	isProCover,
	isRaex,
	isRgbwwPmRelatedChannel,
	isWallDisplay,
	isYihao,
	is_gen_2,
} from 'src/helpers/interface_helpers';
import { capFirstChar, indexToAlphabet } from 'src/helpers/utilities';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { buildCameraConditions } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
import { VENTORA_FAN_SOURCE_VALUES } from 'src/components/Scenes/helpers/ventora_fan_scenes_helpers';

//i18n translation for all value -> update the all arrays to support it
const operators = ['less than', 'more than'];
const more_than = ['more than'];
const turn_states = ['on', 'off'];
const bolt_states = ['open', 'day_lock', 'night_lock'];
const open_close = ['open', 'close'];
const roller_directions = ['open', 'stop', 'close'];
const occupancy_modes = ['occupied', 'vacant'];
const luxury = ['dark', 'twilight', 'bright']; //dark, twilight, bright - motion// dark, twilight or bright - door 1/2
export const ILLUMINATION_BANDS = luxury as ReadonlyArray<string>;
const sensor_detection = ['detected', 'not detected'];
const pushes_short_long = ['short', 'long'];
const pushes_single_short = ['short'];
const pushes_short_long_double_short = [...pushes_short_long, 'double_short']; // ['short', 'long', 'double_short']
const pushes_short_long_double_short_triple_short = [...pushes_short_long_double_short, 'triple_short']; // ['short', 'long', 'double_short', 'triple_short']
const pushes_extended = [...pushes_short_long_double_short_triple_short, 'short_long', 'long_short']; // ['short', 'long', 'double_short', 'triple_short', 'short_long', 'long_short']
const media_states = ['Radio_Play', 'Radio_Stop'];
const scroll_position = {
	name: 'scroll_position',
	values: ['L', 'R'],
	// Map values to display labels
	valueLabels: {
		L: 'scroll_up',
		R: 'scroll_down',
	},
}; //This is for scroll position remote control

//the title for all deviice conditions must be translated via i18n tool
const _TURN_STATE = {
	title: 'collapse_title_output_state',
	props: {
		id: 'state',
		values: turn_states,
		i18tr: 'turn',
	},
};

const _TURN = {
	title: 'collapse_title_output_state',
	props: {
		id: 'turn',
		values: turn_states,
		i18tr: 'turn',
	},
};

const _ROLLER_POS = {
	title: 'collapse_title_roller_position',
	props: {
		id: 'roller position',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-blinds',
		i18tr: 'roller_position',
	},
};

const _ROLLER_STATE = {
	title: 'collapse_title_roller_state',
	props: {
		id: 'roller state',
		values: roller_directions,
		i18tr: 'roller_last_state',
	},
};

const _ROLLER_LAST_DIR = {
	title: 'collapse_title_roller_direction',
	props: {
		id: 'roller direction',
		values: open_close,
		i18tr: 'roller_direction',
	},
};

const _COVER_POS = {
	title: 'collapse_title_device_position',
	props: {
		id: 'roller position',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-blinds',
		i18tr: 'device_pos',
	},
};

const _COVER_STATE = {
	title: 'collapse_title_opening_state',
	props: {
		id: 'roller state',
		values: ['open', 'opening', 'closed', 'closing', 'stopped'], //includes also, but not implementes here (UI) -> opening, closing
		i18tr: 'opening_state',
	},
};

/**Extract separate conditions for multichannel cover, because of scenes' format, that includes channel */
const _MULTI_COVER_STATE = {
	title: 'collapse_title_opening_state',
	props: {
		id: 'multi_cover_state',
		values: ['open', 'opening', 'closed', 'closing', 'stopped'], //includes also, but not implementes here (UI) -> opening, closing
		i18tr: 'opening_state',
	},
};

const _MULTI_COVER_POS = {
	title: 'collapse_title_device_position',
	props: {
		id: 'multi_cover_position',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-blinds',
		i18tr: 'device_pos',
	},
};

const _MEDIA_STATE = {
	title: 'collapse_title_media_state',
	props: {
		id: 'media_state',
		values: media_states,
		i18tr: 'media_state',
	},
};

const _INPUT_VALUE = (disabled, index) => {
	return {
		title: 'collapse_title_input_state',
		props: {
			id: 'input_value',
			values: turn_states,
			disabled,
			i18tr: 'input_turn',
			input_index: index,
			//add_info_ind: index
		},
	};
};

const _INPUT_READER = (values: string[], disabled: boolean = false, index) => {
	return {
		title: 'collapse_title_push_type',
		props: {
			id: 'push_type',
			disabled,
			icon: 'fa fa-hand-back-point-up',
			// add_info_ind: index,
			input_index: index,
			values,
			i18tr: 'push_type',
		},
	};
};

const _TEMPERATURE = {
	title: 'collapse_title_temperature_detection',
	props: {
		id: 'temperature',
		values: operators,
		input_value: 0,
		character: 'C',
		icon: 'fa fa-temperature-half',
		i18tr: 'temperature',
	},
};

const _TEMPERATURE_WALL_DISPLAY = {
	..._TEMPERATURE,
	props: {
		..._TEMPERATURE.props,
		add_info_ind: 0,
	},
};

const _HUMIDITY = {
	title: 'collapse_title_humidity_detection',
	props: {
		id: 'humidity',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-humidity',
		i18tr: 'humidity',
	},
};

const _HUMIDITY_WALL_DISPLAY = {
	..._HUMIDITY,
	props: {
		..._HUMIDITY.props,
		add_info_ind: 0,
	},
};

const _MOISTURE = {
	title: 'icon_name_moisture',
	props: {
		id: 'moisture',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-droplet-percent',
		i18tr: 'moisture',
	},
};

const _BRIGHTNESS = {
	title: 'collapse_title_brightness',
	props: {
		id: 'brightness',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-lightbulb',
		i18tr: 'brightness',
	},
};

const _WHITE_CHANNEL = {
	title: 'label_white_channel',
	props: {
		id: 'white_channel',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa fa-lightbulb',
		i18tr: 'white_channel',
	},
};

const _BATTERY = {
	title: 'collapse_title_battery',
	props: {
		id: 'battery',
		values: operators,
		input_value: 0,
		character: '%',
		icon: 'fa-duotone fa-light fa-battery-three-quarters',
		iconStyle: '--fa-secondary-color: var(--color-3); --fa-secondary-opacity: 1;',
		i18tr: 'battery',
	},
};

const _ILLUMINATION_NUMBER = {
	title: 'collapse_title_illumination',
	props: {
		id: 'illumination',
		values: operators,
		input_value: 0,
		character: '',
		icon: 'fa fa-lightbulb',
		i18tr: 'illumination',
	},
};

const _LUX = {
	title: 'collapse_title_luxury',
	props: {
		id: 'luxury',
		values: luxury,
		i18tr: 'luxury',
	},
};

const _TILT = {
	title: 'collapse_title_tilt',
	props: {
		id: 'tilt',
		values: operators,
		character: '°',
		input_value: 0,
		i18tr: 'tilt',
	},
};

const _DOOR = {
	title: 'collapse_door_state',
	props: {
		id: 'door',
		values: open_close,
		i18tr: 'door state',
	},
};

const _GAS = {
	title: 'collapse_title_gas',
	props: {
		id: 'gas',
		values: ['detected'],
		i18tr: 'gas',
	},
};

const _PIR = {
	title: 'collapse_title_motion',
	props: {
		id: 'pir',
		values: sensor_detection,
		i18tr: 'motion',
	},
};

const _PPM = {
	title: 'collapse_title_ppm_detection',
	props: {
		//support only more than condition in OLD App
		id: 'ppm',
		values: more_than,
		input_value: 0,
		character: '',
		i18tr: 'ppm',
	},
};

const _PRESSURE = {
	title: 'icon_name_pressure',
	props: {
		id: 'pressure',
		values: operators,
		character: 'hPa',
		input_value: 1000,
		i18tr: 'pressure',
	},
};

const _DEWPOINT = {
	title: 'label_dewpoint',
	props: {
		id: 'dewpoint',
		values: operators,
		character: '°C',
		input_value: 0,
		i18tr: 'dewpoint',
	},
};

const _PRECIPITATION = {
	title: 'opt_chance_rainfall',
	props: {
		id: 'precipitation',
		values: operators,
		character: 'mm',
		input_value: 0,
		i18tr: 'rainfall',
	},
};

const _WIND_SPEED = {
	title: 'opt_wind_speed',
	props: {
		id: 'speed',
		values: operators,
		character: 'm/s',
		input_value: 0,
		i18tr: 'wind_speed',
	},
};

const _GUEST_SPEED = {
	title: 'label_gust',
	props: {
		..._WIND_SPEED.props,
		id: 'gust_speed',
		i18tr: 'gust_speed',
	},
};

const _UV = {
	title: 'title_uv_index_card',
	props: {
		id: 'uv',
		values: operators,
		character: 'UV',
		input_value: 0,
		i18tr: 'uv',
	},
};

const _DIRECTION = {
	title: 'option_scene_weather_cond_wind_direction',
	props: {
		id: 'direction',
		values: operators,
		character: '°',
		input_value: 0,
		i18tr: 'wind_direction',
	},
};

const _RAIN_STATUS = {
	title: 'label_rain_status',
	props: {
		id: 'moisture_alarm',
		values: sensor_detection,
		i18tr: 'moisture_alarm',
	},
};

/** Boolean liquid-detected / clear flag (`moisture_alarm:0`). Same scene command as rain status. */
const _LIQUID_LEVEL = {
	title: 'label_liquid_level',
	props: {
		id: 'moisture_alarm',
		values: sensor_detection,
		i18tr: 'liquid_level',
	},
};

//the same as for input readers, when handle alert message and decide how to show all of them, delete this and use one single function
const _INPUT_VALUE_RELAY = (
	disabled: boolean,
	withIndForTranslate: boolean = false,
	index: number = 1,
) => {
	if (!withIndForTranslate) {
		return {
			title: 'collapse_title_input_state',
			props: {
				id: 'input_value',
				values: turn_states,
				i18tr: 'input_turn',
				disabled,
				input_index: index,
				// add_info_ind: index
			},
		};
	}

	return {
		title: 'collapse_title_input_state_by_channel',
		type: 'i18n_num_replace',
		values: { n: index + 1 },
		props: {
			id: 'input_value',
			input_index: index,
			// add_info_ind: index,
			values: turn_states,
			i18tr: 'input_turn',
			disabled,
		},
	};
};

/**
 *
 * @param {*} values array of possible push types
 * @param {*} disabled  is it disabled checkbox
 * @param {*} index how elements must return for this type (the count of element in device page)
 * @returns {}
 */
const _INPUT_READER_RELAY = (
	values: string[],
	disabled: boolean,
	withIndForTranslate: boolean = false,
	index: number = 1,
) => {
	if (!withIndForTranslate) {
		return {
			title: 'collapse_title_push_type',
			props: {
				id: 'push_type',
				disabled,
				values,
				i18tr: 'push_type',
				// add_info_ind: index

				input_index: index,
			},
		};
	}
	return {
		title: 'collapse_title_push_type_by_channel',
		type: 'i18n_num_replace',
		values: { n: index + 1 },
		props: {
			id: 'push_type',
			// add_info_ind: index,
			input_index: index,
			disabled,
			values,
			i18tr: 'push_type',
		},
	};
};

const _SENSOR = (name) => {
	return {
		title: 'prop_' + name,
		props: {
			id: name,
			values: sensor_detection,
			i18tr: name,
		},
	};
};

const _SCROLL_POSITION = {
	title: 'prop_scroll_direction',
	props: {
		id: scroll_position.name,
		values: scroll_position.values,
		i18tr: 'scroll_position',
	},
};

const _SCROLL_STEPS = {
	title: 'prop_scroll_steps',
	props: {
		id: 'scroll_steps',
		values: operators,
		input_value: 0,
		character: '',
		icon: 'fa fa-arrow-up-arrow-down',
		i18tr: 'scroll_steps',
	},
};

const _METER = (meters = 1, channel) => {
	if (meters === 1) {
		return [
			{
				title: `collapse_title_power`,
				props: {
					id: 'meter',
					// add_info_ind: Number(channel),

					input_index: Number(channel),
					values: operators,
					input_value: 0,
					character: 'W',
					icon: 'fa fa-bolt',
					i18tr: 'power_meter',
				},
			},
		];
	}

	let arr = [];
	for (let i = 0; i < meters; i++) {
		arr.push({
			title: 'option_power_measured_on_phase',
			type: 'i18n_num_replace',
			values: { phase: indexToAlphabet(i) },
			props: {
				id: 'meter',
				input_index: i,
				//add_info_ind: i, //use it for select condition unique id
				// meter_ind: i,
				values: operators,
				input_value: 0,
				character: 'W',
				icon: 'fa fa-meter-bolt',
				i18tr: 'power_meter',
			},
		});
	}
	return arr;
};

const _VOL_METER = (meters = 1, channel) => {
	if (meters === 1) {
		return [
			{
				title: `collapse_title_voltage`,
				props: {
					id: 'vol_meter',
					values: operators,
					input_value: 0,
					input_index: Number(channel),
					// add_info_ind: Number(channel),
					character: 'V',
					icon: 'fa fa-gauge-circle-bolt',
					i18tr: 'voltage_meter',
				},
			},
		];
	}

	let arr = [];
	for (let i = 0; i < meters; i++) {
		arr.push({
			title: 'option_voltage_measured_by_phase',
			type: 'i18n_num_replace',
			values: { phase: indexToAlphabet(i) },
			props: {
				id: 'vol_meter',
				input_index: i,
				//add_info_ind: i, //use it for select condition unique id
				// meter_ind: i,
				values: operators,
				input_value: 0,
				character: 'V',
				icon: 'fa fa-gauge-circle-bolt',
				i18tr: 'voltage_meter',
			},
		});
	}
	return arr;
};

const _TARIFF = (channel) => [
	{
		title: 'collapse_title_tariff',
		props: {
			id: 'tariff',
			input_index: Number(channel),
			operators: ['equal'],
			values: ['T1', 'T2', 'T3', 'T4'],
			icon: 'fa fa-tags',
			i18tr: 'tariff',
		},
	},
];

const _TOTAL_CURRENT = (alert) => {
	return {
		title: 'option_power_measured_sum_of_phases',
		props: {
			id: 'total_current',
			values: operators,
			input_value: 0,
			icon: 'fa fa-meter-bolt',
			i18tr: 'total_current',
			character: 'W',
			alert,
		},
	};
};

export const _ADD_ONS = (addOns) => {
	let add_ons_arr = [];
	if (addOns.temperature) {
		for (const temp of addOns.temperature) {
			add_ons_arr.push({
				title: 'collapse_title_temperature_detection',
				props: {
					id: 'temperature',
					sensor_name: temp.name,
					add_info_ind: temp.extid,
					i18tr: 'temperature',
					values: operators,
					input_value: 0,
					character: temp.unit || '°C',
					icon: 'fa fa-temperature-half',
				},
			});
		}
	}
	if (addOns.humidity) {
		for (const hum of addOns.humidity) {
			add_ons_arr.push({
				title: 'collapse_title_humidity_detection',
				props: {
					id: 'humidity',
					sensor_name: hum.name,
					add_info_ind: hum.extid,
					i18tr: 'humidity',
					values: operators,
					input_value: 0,
					character: '%', //temp.unit,
					icon: 'fa fa-temperature-half',
				},
			});
		}
	}
	if (addOns.voltmeter) {
		for (const _voltMeter of addOns.voltmeter) {
			add_ons_arr.push({
				title: 'collapse_title_voltage',
				props: {
					id: 'vol_meter_add_on',
					sensor_name: _voltMeter.name,
					add_info_ind: _voltMeter.extid,
					values: operators,
					input_value: 0,
					character: 'V',
					icon: 'fa fa-gauge-circle-bolt',
					i18tr: 'voltage_meter',
				},
			});
		}
	}
	if (addOns.input) {
		for (const input of addOns.input) {
			if (input.unit === '') {
				//switch or button type
				add_ons_arr.push({
					title: 'collapse_title_input_state',

					props: {
						id: 'digital_input_add_on',
						sensor_name: input.name,
						add_info_ind: input.extid,
						values: turn_states,
						icon: input.icon,
						disabled: input.inputType !== 'switch', //|| (input.value !== 'ON' && input.value !== 'OFF'),
						i18tr: 'input_turn',
					},
				});

				add_ons_arr.push({
					title: 'collapse_title_push_type',
					props: {
						id: 'push_type_add_on',
						sensor_name: input.name,
						add_info_ind: input.extid,
						values: pushes_short_long_double_short_triple_short,
						icon: input.icon,
						disabled: input.inputType !== 'button', // || input.value === 'ON' || input.value === 'OFF',
						i18tr: 'push_type',
					},
				});
			} else {
				add_ons_arr.push({
					title: 'collapse_title_digital_input_percent',
					props: {
						id: 'analog_input_add_on',
						sensor_name: input.name,
						add_info_ind: input.extid,
						input_value: 0,
						values: operators,
						icon: input.icon,
						i18tr: 'analog_input_percent',
						character: '%',
					},
				});
			}
		}
	}

	if (addOns.switch) {
		for (const addon of addOns.switch) {
			add_ons_arr.push({
				title: 'collapse_title_output_state',

				props: {
					id: 'turn_add_on',
					sensor_name: addon.name,
					add_info_ind: addon.extid,
					values: turn_states,
					icon: addon.icon,
					// addon.inputType returns 'none' for now
					// disabled: addon.inputType !== 'switch',//|| (input.value !== 'ON' && input.value !== 'OFF'),
					i18tr: 'turn_add_on',
				},
			});
		}
	}

	return add_ons_arr;
};

const _ADD_EXT_SWITCH = (ext_sw_obj) => {
	let arr = [];
	for (let _k in ext_sw_obj) {
		arr.push({
			title: 'collapse_title_ext_switch',
			props: {
				id: 'ext_switch',
				add_info_ind: Number(_k), //use it as unique id for condition in slect list
				// extSwitchId: Number(_k),
				i18tr: 'ext_switch',
				values: turn_states,
			},
		});
	}
	return arr;
};

const _ADD_ADCS = () => {
	return {
		title: 'collapse_title_adcs_voltage',
		props: {
			id: 'adc',
			values: operators,
			i18tr: 'adc',
			input_value: 0,
			icon: false,
			character: '',
		},
	};
};

const _VALVE_OPEN_STATE = {
	title: 'collapse_title_valve_open_state',
	props: {
		//support only more than condition in OLD App
		id: 'valve_open_state',
		operators: ['equal'],
		values: ['open', 'closed'],
		i18tr: 'valve_open_state',
	},
};

const _BLE_REPORTED_BY = (deviceList) => {
	return {
		title: 'collapse_title_reported_by',
		props: {
			id: 'reported_by',
			operators: ['equal'],
			list: [...deviceList],
			i18tr: 'reported_by',
		},
	};
};

const _WINDOW = {
	title: 'collapse_door_state',
	props: {
		id: 'window',
		values: open_close,
		i18tr: 'door state',
	},
};

const _DISTANCE = {
	title: 'collapse_distance_state',
	props: {
		id: 'dist',
		values: operators,
		i18tr: 'distance',
		input_value: 0,
		character: 'mm',
		icon: 'fa-solid fa-ruler-horizontal',
	},
};

const _PERCENT = {
	title: 'collapse_percent_state',
	props: {
		id: 'percent',
		values: operators,
		i18tr: 'percent',
		input_value: 0,
		character: '%',
		icon: 'fa-solid fa-percent',
	},
};

const _OCCUPANCY = {
	title: 'label_occupancy',
	props: {
		id: 'occupancy',
		values: occupancy_modes,
		i18tr: 'occupancy',
	},
};

const _INTENSITY_LEFT = {
	title: 'prop_intensity_left',
	props: {
		id: 'intensity_left',
		values: operators, // ['less than', 'more than']
		input_value: 0,
		character: '%',
		icon: 'fa fa-gauge-circle-bolt',
		i18tr: 'intensity_left',
	},
};

const _INTENSITY_RIGHT = {
	title: 'prop_intensity_right',
	props: {
		id: 'intensity_right',
		values: operators, // ['less than', 'more than']
		input_value: 0,
		character: '%',
		icon: 'fa fa-gauge-circle-bolt',
		i18tr: 'intensity_right',
	},
};

const _LEVEL_LEFT = {
	title: 'collapse_title_level_left',
	props: {
		id: 'level_left',
		values: operators, // ['less than', 'more than']
		input_value: 0,
		character: '%',
		icon: 'fa fa-droplet',
		i18tr: 'level_left',
	},
};

const _LEVEL_RIGHT = {
	title: 'collapse_title_level_right',
	props: {
		id: 'level_right',
		values: operators, // ['less than', 'more than']
		input_value: 0,
		character: '%',
		icon: 'fa fa-droplet',
		i18tr: 'level_right',
	},
};

// const _AMBIENT_ENABLE = {//This scene isn't triggering yet
// 	title: 'collapse_title_ambient_enable',
// 	props: {
// 		id: 'ambient_enable',
// 		values: turn_states, // ['on', 'off']
// 		i18tr: 'ambient_enable',
// 		icon: 'fa fa-lightbulb',
// 	},
// };

const DIFFUSER_CONFIG = {
	'S4PB-00CU000002': [_INTENSITY_LEFT, _INTENSITY_RIGHT, _LEVEL_LEFT, _LEVEL_RIGHT],
};

const SENSORS_CONFIG = {
	'SHHT-1': [_TEMPERATURE, _HUMIDITY, _BATTERY],
	HT1NB: [_TEMPERATURE, _HUMIDITY, _BATTERY],
	'SHSM-01': [_SENSOR('smoke'), _TEMPERATURE, _BATTERY], // "smoke" = 'true', 'false',
	'SHGS-1': [_GAS, _PPM], // "gas" = unknown, none, mild, heavy, test
	'SHDW-1': [_DOOR, _LUX, _ILLUMINATION_NUMBER, _BATTERY], // "door" = on/off, "lux_i" = number, lux = string
	'SHDW-2': [_DOOR, _TILT, _SENSOR('vibration'), _LUX, _TEMPERATURE, _ILLUMINATION_NUMBER, _BATTERY], // vibr = on/off;,
	//Because of OLD APP -> support only motion property
	// 'SHMOS-01': [_SENSOR('motion'), _SENSOR('vibration'), _ILLUMINATION_NUMBER, _LUX, _BATTERY], //"motion" = on/off, "virb" = on/off, "lux_i" = number, lux = string
	'SHMOS-01': [_SENSOR('motion'), _SENSOR('vibration'), _LUX, _BATTERY], //"motion" = on/off, "virb" = on/off, "lux_i" = number, lux = string
	'SHMOS-02': [_SENSOR('motion'), _SENSOR('vibration'), _LUX, _TEMPERATURE, _BATTERY],
	'SHWT-1': [_SENSOR('flood'), _TEMPERATURE, _BATTERY], //"flood" = true/ false
	SMK1NB: [_SENSOR('smoke'), _BATTERY], // "smoke" = true/false
	WS1NB: [_SENSOR('flood'), _BATTERY], //"flood" = true/false
	'SHSEN-1': [_PIR, _TEMPERATURE, _HUMIDITY, _LUX, _ILLUMINATION_NUMBER, _BATTERY], // pir is a "motion" = on/off, others are numbers

	//GEN 2 sensors
	'SNSN-0013A': [_TEMPERATURE, _HUMIDITY, _BATTERY], //H&T2
	'SNSN-0031Z': [_SENSOR('smoke'), _BATTERY], //Shelly Plus Smoke
	'S3SN-0082A': [_SENSOR('smoke'), _BATTERY], //Shelly Alarm Bridge
	//GEN 3-4 sensors
	'S4SN-1031Z': [_SENSOR('smoke'), _BATTERY], //Heiman Smoke EU
	'S4SN-0031Z': [_SENSOR('smoke'), _BATTERY], //Heiman Smoke
	'S4SN-0071A': [_SENSOR('flood'), _BATTERY], //Shelly Flood G4
	'S4SN-0071Z': [_SENSOR('flood'), _BATTERY], //Shelly Flood S G4
	'SMSN-0031ZL': [_SENSOR('smoke'), _BATTERY], //Smoke LTE
	//GEN 3 sensors
	'S3SN-0U12A': [_TEMPERATURE, _HUMIDITY, _BATTERY], //H&T2
	'S3SN-1U12A': [_TEMPERATURE, _HUMIDITY, _BATTERY], //H&T G3 FFS
	//GEN BLE
	'SBHT-003C': [_TEMPERATURE, _HUMIDITY, _BATTERY], //H&TBLU
	'SBHT-103C': [_TEMPERATURE, _HUMIDITY, _BATTERY, _LUX], //H&TBLU Display
	'SBHT-203C': [_TEMPERATURE, _HUMIDITY, _BATTERY], //H&TBLU ZB
	//door window types
	'SBDW-002C': [_WINDOW, _TILT, _ILLUMINATION_NUMBER, _BATTERY],
	'SBDW-103C': [_WINDOW, _TILT, _LUX, _BATTERY], // BLU Door Window ZB — light level over Zigbee
	'SBDW-203C': [_WINDOW, _TILT, _LUX, _BATTERY], // BLU Door Window Mini ZB — light level over Zigbee
	'SBDW-302C': [_WINDOW, _TILT, _BATTERY], // BLU Door Window Outdoor ZB — light level over Zigbee
	'SBDW-002D': [_WINDOW, _TILT, _ILLUMINATION_NUMBER, _BATTERY],
	'SBMO-003Z': [_SENSOR('motion'), _ILLUMINATION_NUMBER, _BATTERY],
	'SBMO-103Z': [_SENSOR('motion'), _LUX, _BATTERY], // BLU Motion ZB — light level over Zigbee
	//distance
	'SBDI-003E': [_PERCENT, _OCCUPANCY, _DISTANCE, _SENSOR('vibration'), _BATTERY], // BLU Distance
	'SBMS-001A': [_MOISTURE, _TEMPERATURE, _BATTERY], // BLU Soil
	'SBSN-001B': [_LIQUID_LEVEL, _BATTERY], // BLU Liquid Level Sensor ZB
	//Weather Station
	'SBWS-90CM': [
		_TEMPERATURE,
		_HUMIDITY,
		_PRESSURE,
		_DEWPOINT,
		_PRECIPITATION,
		_ILLUMINATION_NUMBER,
		_WIND_SPEED,
		_GUEST_SPEED,
		_UV,
		_DIRECTION,
		_RAIN_STATUS,
		_BATTERY,
	],
	//Presence sensor S4SN-0U61X — luminosity bands only (no numeric lux threshold in scenes)
	'S4SN-0U61X': [_LUX], // Shelly Presence G4
	'S3SN-0U53X': [], // The Pill by Shelly
};

const BLE_SENSORS_CONFIG = (type, deviceList = []) => {
	if (SENSORS_CONFIG[type] !== undefined) {
		return [...SENSORS_CONFIG[type], _BLE_REPORTED_BY(deviceList)];
	}
};

/**
 * Non-Zigbee BLU devices that, starting with FW 1.0.24, broadcast the standardized
 * BTHome "light level" object (0x64) instead of raw lux. From this version on, the
 * scene condition exposes discrete light-level bands (dark / twilight / bright) via
 * `_LUX` rather than the numeric `_ILLUMINATION_NUMBER`. Below the version they keep
 * the legacy numeric illuminance condition.
 *
 * The Zigbee (ZB) variants (SBDW-103C, SBDW-203C, SBDW-302C, SBMO-103Z) already report
 * light level over the Zigbee cluster regardless of firmware, so they are configured
 * with `_LUX` directly in `SENSORS_CONFIG` and are intentionally excluded here.
 *
 * @see FW-1690 / FE-1174
 */
const LIGHT_LEVEL_BAND_MODELS = ['SBDW-002C', 'SBMO-003Z'];
const LIGHT_LEVEL_BAND_MIN_VER = '1.0.24';

/**
 * For the affected non-ZB BLU light-level devices on FW >= 1.0.24, replace the numeric
 * illuminance condition with the discrete light-level bands condition (mirroring
 * BLU HT Display ZB / SBHT-103C). Any duplicate illuminance entries collapse into a
 * single `_LUX` band condition, preserving the original position. Devices below the
 * version (or with an unknown version) keep their current conditions unchanged.
 */
const applyLightLevelBands = (type: string, config, fwVersion?: string) => {
	if (!config || !LIGHT_LEVEL_BAND_MODELS.includes(type)) return config;
	if (!fwVersion || !compareFWVersions(fwVersion, '>=', LIGHT_LEVEL_BAND_MIN_VER)) return config;

	let bandAdded = false;
	return config.reduce((acc, condition) => {
		const id = condition?.props?.id;
		if (id === 'illumination' || id === 'luxury') {
			if (!bandAdded) {
				acc.push(_LUX);
				bandAdded = true;
			}
			return acc;
		}
		acc.push(condition);
		return acc;
	}, []);
};

const _VALVE_OPEN_PERCENT = {
	title: 'collapse_title_valve_percent_value',
	props: {
		id: 'valve_percent_position',
		values: operators,
		input_value: 0,
		icon: false,
		character: '%',
		i18tr: 'valve_percent_opening',
	},
};

const _TRV_TARGET_TEMPERATURE = {
	title: 'collapse_title_target_temperature',
	props: {
		id: 'trv_target_temperature',
		values: operators,
		input_value: 0,
		character: 'C',
		icon: 'fa fa-crosshairs',
		i18tr: 'trv_target_temperature',
	},
};

const TRV_CONFIG = [_TEMPERATURE, _VALVE_OPEN_STATE, _VALVE_OPEN_PERCENT, _BATTERY];

const _SOLAR_INPUT_POWER = {
	title: 'collapse_title_solar_input_power',
	props: {
		id: 'solar_input_power',
		values: operators,
		input_value: 0,
		icon: 'fa fa-solar-panel',
		character: 'W',
		i18tr: 'solar_input_power',
	},
};

const _OUTPUT_HOME_POWER = {
	title: 'collapse_title_output_home_power',
	props: {
		id: 'output_home_power',
		values: operators,
		input_value: 0,
		icon: 'fa fa-house-day',
		character: 'W',
		i18tr: 'output_home_power',
	},
};

const _BATTERY_OUTPUT_LIMIT = {
	title: 'collapse_title_outputLimit',
	props: {
		id: 'outputLimit',
		values: operators,
		input_value: 0,
		icon: 'fa fa-car-battery',
		character: 'W',
		i18tr: 'outputLimit',
	},
};

const _BATTERY_ELECTRIC_LEVEL = {
	title: 'collapse_title_electric_level',
	props: {
		id: 'electricLevel',
		values: operators,
		input_value: 0,
		icon: 'fa fa-battery-bolt',
		character: '%',
		i18tr: 'electricLevel',
	},
};

const BLUTRV_CONFIG = [
	_TEMPERATURE,
	_TRV_TARGET_TEMPERATURE,
	_VALVE_OPEN_STATE,
	_VALVE_OPEN_PERCENT,
	_BATTERY,
];

const BLUTRV_CONFIG_NOT_CALIBRATED = [_TEMPERATURE, _TRV_TARGET_TEMPERATURE, _BATTERY];

const _BOLT_STATE = {
	title: 'Bolt State',
	props: {
		id: 'bolt_state',
		values: bolt_states,
		i18tr: 'bolt_state',
	},
};

const _FAN_ON = {
	title: 'collapse_title_output_state',
	props: {
		id: 'fan_on',
		values: turn_states,
		i18tr: 'fan_on',
	},
};

const _FAN_SPEED = {
	title: 'ventora_status_speed',
	props: {
		id: 'fan_speed',
		values: operators,
		input_value: 50,
		character: '%',
		icon: 'fas fa-tachometer-alt',
		i18tr: 'fan_speed',
	},
};

const _FAN_SOURCE = {
	title: 'scene_condition_fan_source',
	props: {
		id: 'fan_source',
		values: [...VENTORA_FAN_SOURCE_VALUES],
		i18tr: 'fan_source',
	},
};

export const _init_device_condition = {
	relay: (
		type,
		gen = 1,
		channel: number = 0,
		meters,
		hasInputs,
		btn_type: undefined | string[],
		addOns = undefined,
		ext_sw_obj = undefined,
		adc = undefined,
		hasIlluminance = undefined,
	) => {
		let arr = [_TURN];

		// console.log('has meters ', meters);
		//Because we have gen: 0, 1, 2???
		//and String and Number values , also in integratiosn comes {}
		if (is_gen_2({ gen })) {
			if (hasInputs && Array.isArray(btn_type)) {
				for (let index = 0; index < btn_type.length; index++) {
					arr.push(
						_INPUT_VALUE_RELAY(
							btn_type[index] !== 'switch',
							btn_type.length > 1,
							Number(channel) > 0 ? Number(channel) : Number(index),
						),
						_INPUT_READER_RELAY(
							pushes_short_long_double_short_triple_short,
							btn_type[index] === 'switch',
							btn_type.length > 1,
							Number(channel) > 0 ? Number(channel) : Number(index),
						),
					);
				}
			}

			if (addOns && Object.keys(addOns).some((key) => addOns[key].length > 0)) {
				arr.push(..._ADD_ONS(addOns));
			}
			if (isWallDisplay(type)) {
				arr.push(_TEMPERATURE_WALL_DISPLAY, _HUMIDITY_WALL_DISPLAY);
			}
			if (hasIlluminance) {
				arr.push(_LUX);
			}
			if (meters) {
				arr.push(..._METER(1, channel));
			}
		} else {
			let disabled = (type) => !['momentary', 'momentary_on_release'].includes(type);
			/**Check is there an input prop in status
			 * - Plugs and Old 4PRO not in the case with phisical inputs
			 * - Special case 1L - 2 inputs, 1 output with 2 different btn_types for every single input
			 */
			if (hasInputs && Array.isArray(btn_type)) {
				for (let ind = 0; ind < btn_type.length; ind++) {
					arr.push(
						_INPUT_VALUE_RELAY(
							btn_type[ind] === 'detached' ? false : !disabled(btn_type[ind]),
							btn_type.length > 1,
							Number(channel) > 0 ? Number(channel) : Number(ind),
						),
						_INPUT_READER_RELAY(
							pushes_short_long,
							btn_type[ind] === 'detached' ? false : disabled(btn_type[ind]),
							btn_type.length > 1,
							Number(channel) > 0 ? Number(channel) : Number(ind),
						),
					);
				}
			}
			if (addOns && (addOns?.temperature?.length > 0 || addOns?.humidity?.length > 0))
				arr.push(..._ADD_ONS(addOns));
			if (ext_sw_obj) arr.push(..._ADD_EXT_SWITCH(ext_sw_obj));
			if (adc) arr.push(_ADD_ADCS());
			// Additional check 'isPM' for showing 'Power Consumption' option
			const isPM = devices_config[type].isPM;
			if (meters && isPM) arr.push(..._METER(1, channel));
		}

		return arr;
	},
	module: (channel: number = 0, meters, hasInputs, btn_type: undefined, mode, addOns = undefined) => {
		let arr = [];
		if (mode === 'inputs_reader') {
			arr.push(_INPUT_VALUE(btn_type !== 'switch', Number(channel)));
			arr.push(
				_INPUT_READER(
					pushes_short_long_double_short_triple_short,
					btn_type === 'switch',
					Number(channel),
				),
			);

			if (addOns && Object.keys(addOns).some((key) => addOns[key].length > 0)) {
				arr.push(..._ADD_ONS(addOns));
			}
		} else {
			arr.push(_TURN);

			if (hasInputs && typeof btn_type !== 'undefined') {
				arr.push(
					_INPUT_VALUE_RELAY(btn_type !== 'switch', false, Number(channel)),
					_INPUT_READER_RELAY(
						pushes_short_long_double_short_triple_short,
						btn_type === 'switch',
						false,
						Number(channel),
					),
				);
			}

			if (meters) {
				arr.push(..._METER(1, channel));
			}

			if (addOns && Object.keys(addOns).some((key) => addOns[key].length > 0)) {
				arr.push(..._ADD_ONS(addOns));
			}
		}

		return arr;
	},

	roller: (gen = 1, type, addOns = {}, channel) => {
		if (isProCover(type)) {
			return [_MULTI_COVER_STATE, _MULTI_COVER_POS, ..._METER(1, channel)];
		}
		if (['S3SH-0A2P4EU'].includes(type)) {
			// shelly shutter
			return [_MULTI_COVER_STATE, _MULTI_COVER_POS, ..._METER(1, channel), ..._ADD_ONS(addOns)];
		}
		if (isYihao(type) || isRaex(type)) {
			return [_MULTI_COVER_STATE, _MULTI_COVER_POS, ..._ADD_ONS(addOns)];
		}
		if (is_gen_2({ gen })) {
			// ToDo: Rework these abstracted methods Too many checks for generation.
			return [_COVER_STATE, _COVER_POS, ..._METER(1, 0), ..._ADD_ONS(addOns)];
		} else {
			return [_ROLLER_STATE, _ROLLER_POS, _ROLLER_LAST_DIR, ..._METER(1, 0)];
		}
	},

	emeter: (type, channel, alert, mode, addOns = {}) => {
		switch (type) {
			case 'SPEM-002CEBEU50':
			case 'S3EM-002CXCEU':
			case 'S4EM-002CXCEU':
			case 'SHEM':
				return [_TURN, ..._METER(1, channel), ..._VOL_METER(1, channel)];
			case 'SHEM-3':
				return [_TURN, ..._METER(3, channel), ..._VOL_METER(3, channel), _TOTAL_CURRENT(alert)];
			case 'SPEM-003CEBEU':
			case 'SPEM-003CEBEU400':
			case 'SPEM-003REBEU2000':
			case 'SPEM-003CEBEU63':
			case 'EFPEM-003CEBEU120':
			case 'S3EM-003CXCEU63':
			case 'S4EM-003RXCEU63':
				if (mode === 'monophase') {
					return [..._METER(1, channel), ..._VOL_METER(1, channel), ..._ADD_ONS(addOns)];
				}

				return [
					..._METER(3, channel),
					..._VOL_METER(3, channel),
					_TOTAL_CURRENT(alert),
					..._ADD_ONS(addOns),
				];
			// EM mini and EM-63 Gen4 do not have output state and are single-phase
			case 'S4EM-001PXCEU16':
			case 'S4EM-001CXCEU63':
				return [..._METER(1, channel), ..._VOL_METER(1, channel)];
			// Daisy EM supports tariff scheduling in addition to power and voltage
			case 'S4PB-06A7000101':
				return [..._METER(1, channel), ..._VOL_METER(1, channel), ..._TARIFF(channel)];
			default:
				return [..._METER(3, channel), ..._VOL_METER(3, channel), _TOTAL_CURRENT(alert)];
		}
	},
	inputs_reader: (
		type,
		btn_type = undefined,
		channel = 0,
		gen = 1,
		addOns = undefined,
		deviceList = [],
	) => {
		const isPM = devices_config[type].isPM; //this must cover all lights with PM support
		let arr = [];

		//https://shelly-api-docs.shelly.cloud/gen1/#shelly-button1-input-0
		if (['SHBTN-1', 'SHBTN-2'].includes(type)) {
			arr.push(_INPUT_READER(pushes_short_long_double_short_triple_short, false, Number(channel)));
			return arr;
		}
		// BLE buttons
		if (BLE_BUTTON_MODELS.includes(type)) {
			/**
			 * looking for observer flag
			 * {"enable":true,"rpc":{"enable":true},"observer":{"enable":true}}
			 * in ble prop for each gen 2 device settings package
			 */
			arr.push(
				_INPUT_READER(
					pushes_short_long_double_short_triple_short,
					btn_type === 'switch',
					Number(channel),
				), //,
				//_BLE_REPORTED_BY(deviceList)
			);
			return arr;
		}

		if (is_gen_2({ gen })) {
			//console.log('device channel ', Number(channel));
			arr.push(
				_INPUT_VALUE(btn_type !== 'switch', Number(channel)),
				_INPUT_READER(
					pushes_short_long_double_short_triple_short,
					btn_type === 'switch',
					Number(channel),
				),
			);
			if (addOns && Object.keys(addOns).some((key) => addOns[key].length > 0)) {
				arr.push(..._ADD_ONS(addOns));
			}
			if (isPM) {
				const addConsumption = _METER(1, channel);
				arr.push(...addConsumption);
			}
			return arr;
		} else {
			//https://shelly-api-docs.shelly.cloud/gen1/#shelly-i3-settings
			arr.push(
				_INPUT_VALUE(btn_type === 'momentary', Number(channel)),
				_INPUT_READER(pushes_extended, btn_type !== 'momentary', Number(channel)),
			);
			if (isPM) {
				const addConsumption = _METER(1, channel);
				arr.push(...addConsumption);
			}
			return arr;
		}
	},
	remote_control: () => {
		let arr = [];

		// Button 1 (input 0)
		arr.push(_INPUT_READER_RELAY(pushes_single_short, false, true, 0));

		// Button 2 (input 1)
		arr.push(_INPUT_READER_RELAY(pushes_single_short, false, true, 1));

		// Scroll position (L/R direction)
		arr.push(_SCROLL_POSITION);
		// Scroll steps (rotie1s): non-negative step count; use scroll_position (rotie1) for direction
		arr.push(_SCROLL_STEPS);

		return arr;
	},
	light: (
		type: string,
		channel: number,
		gen: number,
		mode: string | undefined,
		btn_type_1: any,
		addOns: any,
		deviceRgbwwProfile: string,
	) => {
		const isPM = devices_config[type].isPM; //this must cover all lights with PM support
		const hasWhite = devices_config[type]?.modes?.[mode]?.has_white;

		if (is_gen_2({ gen })) {
			//for dimmer and for +RGBW
			let arr = [_TURN, _BRIGHTNESS];

			if (hasWhite) arr.push(_WHITE_CHANNEL);

			// https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2999#note_473023
			if (isRgbwwPmRelatedChannel(type, deviceRgbwwProfile, channel)) {
				arr = [];
			}

			if (addOns && Object.keys(addOns).some((key) => addOns[key].length > 0)) {
				arr.push(..._ADD_ONS(addOns));
			}

			function isInputDisabled(_channel, index) {
				// This condition satisfying only the devices with type 'SPDM-002PE01EU'
				// When the for loop is iterating we check if the device type is this specific dimmer with two channels
				// This type of dimmer the 1st channel on index 0 have to have access only to the first and second inputs on index 0 and 1
				// the 2nd channel on index 1 have to have access only to inputs on index 2 and 3
				// If the condition is met -> for example: the device type is 'SPDM-002PE01EU', the channel is 0 and the index is 0 or 1
				// then the input is not disabled, so return false
				// also check if the type of the current index is switch or not
				// (this condition is here, because the all inputs have to be visible for all of the channels but only the available have to be enabled.
				// the btn_type is array of strings that give us the types of the inputs. if the condition is where the btn_type is created the btn_type will return only the available inputs for the current channel )

				return (Number(_channel) === 0 && index < 2) || (Number(_channel) === 1 && index >= 2);
			}

			// TODO: Evaluate the need for a herlper to deretmine the corresponding input for a given channel
			// Especially for Pro RGBWW PM

			//add consumption as condition for PM devices
			if (isPM) {
				//https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2999#note_473023
				if (!isRgbwwPmRelatedChannel(type, deviceRgbwwProfile, channel)) {
					const addConsumption = _METER(1, channel);
					arr.push(...addConsumption);
				}
			}

			if (btn_type_1 !== undefined) {
				if (Array.isArray(btn_type_1)) {
					for (let index = 0; index < btn_type_1.length; index++) {
						arr.push(
							_INPUT_VALUE_RELAY(
								type === 'SPDM-002PE01EU'
									? !isInputDisabled(channel, index) || btn_type_1[index] !== 'switch'
									: btn_type_1[index] !== 'switch',
								btn_type_1.length > 1,
								btn_type_1.length > 1 ? Number(index) : Number(channel),
							),
							_INPUT_READER_RELAY(
								pushes_short_long_double_short_triple_short,
								type === 'SPDM-002PE01EU'
									? !isInputDisabled(channel, index) || btn_type_1[index] === 'switch'
									: btn_type_1[index] === 'switch',
								btn_type_1.length > 1,
								btn_type_1.length > 1 ? Number(index) : Number(channel),
							),
						);
					}
				}
				return arr;
			} else {
				return arr;
			}
		} else {
			let arr = [];

			if (isPM) {
				//add consumption as condition for PM devices
				const addConsumption = _METER(1, channel);
				arr.push(...addConsumption);
			}

			if (
				['SHRGBWW-01', 'SHCB-1', 'SHBLB-1', 'SHCL-255'].includes(type) ||
				(type === 'SHRGBW2' && mode === 'color')
			) {
				arr.push(_TURN_STATE, _BRIGHTNESS);
			} else if (['SHDM-1', 'SHDM-2'].includes(type)) {
				//momentaty and momentary_on_relase not supported for dimmers, but for sure

				//Alex: ToDo: Check and refactor this implementation, for one it only checks `btn_type` for the first input...
				const disabled = !['momentary', 'momentary_on_release', 'one_button'].includes(
					btn_type_1[0],
				);

				arr.push(_TURN);
				for (let i = 0; i < 2; i++) {
					arr.push(
						_INPUT_VALUE_RELAY(btn_type_1[0] === 'detached' ? false : disabled, true, i),
						_INPUT_READER_RELAY(
							pushes_short_long,
							btn_type_1[0] === 'detached' ? false : disabled,
							true,
							i,
						),
					);
				}
				arr.push(_BRIGHTNESS);
			} else {
				arr.push(_TURN, _BRIGHTNESS);
			}

			if (hasWhite) arr.push(_WHITE_CHANNEL);

			return arr;
		}
	},
	sensor: (
		type: string,
		gen: number | string,
		deviceList: [],
		deviceSettings?: any,
		modeType?: string,
		fwVersion?: string,
	) => {
		// Special handling for S4SN-0U61X presence sensor
		if (type === 'S4SN-0U61X') {
			return _init_device_condition.presence_sensor(deviceSettings);
		} else if (type === 'SBDI-003E') {
			const idToRemove = modeType === 'occupancy' ? 'percent' : 'occupancy';
			return SENSORS_CONFIG[type].filter((obj) => obj.props.id !== idToRemove);
		}

		// if (gen === 'GBLE') {
		// 	return BLE_SENSORS_CONFIG(type, deviceList)
		// }
		return applyLightLevelBands(type, SENSORS_CONFIG[type], fwVersion);
	},
	thermostat: (type, isCalibrated) => {
		if (isWallDisplay(type)) return [_TEMPERATURE_WALL_DISPLAY, _HUMIDITY_WALL_DISPLAY, _LUX];
		if (type === 'SHTRV-01') return TRV_CONFIG;
		if (type === 'SBTR-001AEU') return isCalibrated ? BLUTRV_CONFIG : BLUTRV_CONFIG_NOT_CALIBRATED; // adding this so, just temporary unsure if there will be a diff. config for this device
		return [];
	},
	power_meter: (channel) => {
		return [..._METER(1, channel), ..._VOL_METER(1, channel)];
	},
	pv: () => {
		let arr = [
			_SOLAR_INPUT_POWER,
			_OUTPUT_HOME_POWER,
			_BATTERY_OUTPUT_LIMIT,
			_BATTERY_ELECTRIC_LEVEL,
		];
		return arr;
	},
	breaker: (channel) => {
		return [_TURN];
	},
	lock: () => {
		return [_BOLT_STATE];
	},
	media_player: () => {
		return [_MEDIA_STATE];
	},
	diffuser: (type) => {
		return DIFFUSER_CONFIG[type] || [];
	},
	fan: () => {
		return [_FAN_ON, _FAN_SPEED, _FAN_SOURCE];
	},

	// Special handler for S4SN-0U61X presence sensor
	presence_sensor: (deviceSettings) => {
		const conditions = [
			// General motion detection
			{
				title: 'collapse_title_motion',
				props: {
					id: 'presence',
					values: operators,
					input_value: 0,
					character: '',
					icon: 'fa fa-person-walking',
					i18tr: 'motion_presence',
				},
			},
			// Luminosity bands (dark / twilight / bright) — numeric lux threshold removed for presence
			_LUX,
		];

		// Add dynamic zone conditions if device settings are available
		if (deviceSettings) {
			Object.keys(deviceSettings).forEach((key) => {
				const match = key.match(/^presencezone:(\d+)$/);
				if (match && match[1] !== '200') {
					// Exclude main zone 200
					const zoneId = match[1];
					const zoneData = deviceSettings[key];
					if (zoneData && zoneData.name) {
						// Zone state condition
						conditions.push({
							title: `Zone ${zoneData.name} State`,
							props: {
								id: 'presence_zone_state',
								add_info_ind: zoneId,
								values: ['true', 'false'],
								i18tr: 'presence_zone_state',
							} as any,
						} as any);

						// Zone object count condition
						conditions.push({
							title: `Zone ${zoneData.name} Objects`,
							props: {
								id: 'presence_zone_objects',
								add_info_ind: zoneId,
								values: operators,
								input_value: 0,
								character: '',
								i18tr: 'presence_zone_objects',
							} as any,
						} as any);

						// Zone enter/leave delta condition
						conditions.push({
							title: `Zone ${zoneData.name} Enter/Leave`,
							props: {
								id: 'presence_zone_objects_delta',
								add_info_ind: zoneId,
								values: operators,
								input_value: 0,
								character: '',
								i18tr: 'presence_zone_objects_delta',
							} as any,
						} as any);
					}
				}
			});
		}

		return conditions;
	},
	camera: (deviceSettings) => {
		return buildCameraConditions(deviceSettings);
	},
	addons: (type, addons) => {
		let arr = [];
		if (addons && Object.keys(addons).some((key) => addons[key].length > 0)) {
			arr.push(..._ADD_ONS(addons));
		}
		return arr;
	},
};

//here the i18n translation must be applied over the name
export const _condition_repeat_type = [
	{ name: 'condition_type_one', _id: 1, hasInputs: true },
	{ name: 'condition_type_any', _id: 2 },
	{ name: 'condition_type_repeat', _id: 3 },
];

export const conditionTypeNameMap = {
	repeat: 'condition_type_repeat',
	any: 'condition_type_any',
	once: 'condition_type_one',
};
export const excludedCategoriesFromWhenConditions = ['gateway'];
export const excludedDeviceTypesFromWhenConditions = ['bthome-device', 'peripheral-device'];

export const diffuserConditions = ['intensity_left', 'intensity_right', 'level_left', 'level_right'];

export const conditionsWithoutNegativeValues = [
	'humidity',
	'moisture',
	'chance_precipitation',
	'fan_speed',
	...diffuserConditions,
];

export function generateNegativeInputValueError(active) {
	const _format = get(format);
	let inputValError = '',
		isValidInputValue = false;

	if (conditionsWithoutNegativeValues.includes(active.cond)) {
		const unit = condition_to_character(active.cond, undefined);
		inputValError =
			active.value &&
			(Number(active.value) < 0
				? capFirstChar(
						_format('input_error_negative_value', {
							default: "{condition} can't be less than 0 {unit}",
							values: {
								condition: _format(`input_error_negative_${active.cond}_value`),
								unit,
							},
						}),
					)
				: Number(active.value) > 100
					? capFirstChar(
							_format('input_error_positive_value', {
								default: "{condition} can't be more than 100 {unit}",
								values: {
									condition: _format(`input_error_negative_${active.cond}_value`),
									unit,
								},
							}),
						)
					: '');

		isValidInputValue = inputValError.length > 0 ? !Boolean(inputValError) : active.value;
		return { inputValError, isValidInputValue };
	} else {
		isValidInputValue = true;
		return { inputValError, isValidInputValue };
	}
}
